/**
 * Bundle Section JS
 * - <pc-gallery> custom element: fade-transition carousel with thumbnails + swipe
 * - Bundle option selection: radio-group behavior, updates hidden quantity input
 */

if (!customElements.get('pc-gallery')) {
    customElements.define(
      'pc-gallery',
      class BundleGallery extends HTMLElement {
        constructor() { 
          super();
          this.currentIndex = 0;
          this.touchStartX = 0;
          this.touchEndX = 0;
        }
  
        connectedCallback() {
          this.slides = this.querySelectorAll('.bundle__gallery-item');
          this.thumbnails = this.querySelectorAll('.bundle__thumbnail-item');
          this.prevBtn = this.querySelector('.bundle__gallery-nav--prev');
          this.nextBtn = this.querySelector('.bundle__gallery-nav--next');
          this.viewer = this.querySelector('.bundle__gallery-viewer');
  
          if (!this.slides.length) return;
  
          this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
          this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
  
          this.thumbnails.forEach((thumb, i) => {
            thumb.querySelector('button')?.addEventListener('click', () => this.goTo(i));
          });
  
          // Swipe support
          if (this.viewer) {
            this.viewer.addEventListener('touchstart', (e) => {
              this.touchStartX = e.changedTouches[0].screenX;
            }, { passive: true });
  
            this.viewer.addEventListener('touchend', (e) => {
              this.touchEndX = e.changedTouches[0].screenX;
              this.handleSwipe();
            }, { passive: true });
          }
  
          this.updateState();
        }
  
        goTo(index) {
          if (index < 0 || index >= this.slides.length) return;
          this.currentIndex = index;
          this.updateState();
        }

        /**
         * Replace gallery + thumbnails with a variant's images.
         * images: [{ full, thumb, alt }]
         */
        setImages(images) {
          if (!Array.isArray(images) || !images.length) return;

          const list = this.querySelector('.bundle__gallery-list');
          const thumbList = this.querySelector('.bundle__thumbnail-list');
          if (!list) return;

          list.innerHTML = images
            .map((img, i) => `<li class="bundle__gallery-item${i === 0 ? ' bundle__gallery-item--active' : ''}"><img src="${img.full}" alt="${img.alt || ''}" loading="${i === 0 ? 'eager' : 'lazy'}"></li>`)
            .join('');

          if (thumbList) {
            thumbList.innerHTML = images
              .map((img, i) => `<li class="bundle__thumbnail-item${i === 0 ? ' bundle__thumbnail-item--active' : ''}"><button type="button" class="bundle__thumbnail-btn" aria-label="Go to item ${i + 1}"><img src="${img.thumb}" alt="${img.alt || ''}" width="80" height="80" loading="lazy"></button></li>`)
              .join('');
          }

          // Re-bind to the new nodes and reset to the first slide.
          this.currentIndex = 0;
          this.slides = this.querySelectorAll('.bundle__gallery-item');
          this.thumbnails = this.querySelectorAll('.bundle__thumbnail-item');
          this.thumbnails.forEach((thumb, i) => {
            thumb.querySelector('button')?.addEventListener('click', () => this.goTo(i));
          });
          this.updateState();
        }
  
        handleSwipe() {
          const diff = this.touchStartX - this.touchEndX;
          const threshold = 50;
          if (Math.abs(diff) < threshold) return;
          if (diff > 0) {
            this.goTo(this.currentIndex + 1);
          } else {
            this.goTo(this.currentIndex - 1);
          }
        }
  
        updateState() {
          this.slides.forEach((slide, i) => {
            slide.classList.toggle('bundle__gallery-item--active', i === this.currentIndex);
          });
  
          this.thumbnails.forEach((thumb, i) => {
            thumb.classList.toggle('bundle__thumbnail-item--active', i === this.currentIndex);
          });
  
          // Centre the active thumbnail by scrolling the thumbnail strip only.
          // scrollIntoView() would also scroll the window, which made the page
          // jump on mobile whenever a swatch swapped the gallery.
          const activeThumb = this.thumbnails[this.currentIndex];
          const thumbList = activeThumb?.parentElement;
          if (activeThumb && thumbList) {
            const listRect = thumbList.getBoundingClientRect();
            const thumbRect = activeThumb.getBoundingClientRect();
            thumbList.scrollTo({
              left: thumbList.scrollLeft + (thumbRect.left - listRect.left) - (listRect.width - thumbRect.width) / 2,
              behavior: 'smooth',
            });
          }
  
          if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
          if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.slides.length - 1;
        }
      }
    );
  }
  
  /**
   * Bundle option selection
   * Handles click on .bundle__option elements within [role=radiogroup]
   */
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.bundle__options').forEach((group) => {
      const options = group.querySelectorAll('.bundle__option');
      const section = group.closest('.bundle');
      const quantityInput = section?.querySelector('input[name="quantity"]');
  
      function selectOption(option) {
        options.forEach((opt) => {
          opt.classList.remove('bundle__option--selected');
          opt.setAttribute('aria-checked', 'false');
        });
  
        option.classList.add('bundle__option--selected');
        option.setAttribute('aria-checked', 'true');
  
        const qty = option.dataset.quantity;
        if (quantityInput && qty) {
          quantityInput.value = qty;
        }
      }
  
      // Sync quantity input with the default selected option on page load
      const defaultSelected = group.querySelector('.bundle__option--selected');
      if (defaultSelected && quantityInput) {
        const qty = defaultSelected.dataset.quantity;
        if (qty) quantityInput.value = qty;
      }

      options.forEach((option) => {
        option.addEventListener('click', () => selectOption(option));

        option.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectOption(option);
          }
        });
      });
    });
  });

  /**
   * <pc-builder> custom element
   *
   * Tier slider that auto-activates based on total selected qty across products.
   * Per-currency anchor prices live on the element as data-base-price-{code}
   * attributes; each tier stores its own discount_percent. The displayed price
   * is anchor * (1 - discount/100) in the active market's currency — no live
   * rate conversion, so prices stay clean whole-number anchors per currency.
   * Checkout builds a /cart/:variants:qty permalink and applies the active
   * tier's discount code if set.
   */
  if (!customElements.get('pc-builder')) {
    customElements.define(
      'pc-builder',
      class BundleBuilder extends HTMLElement {
        constructor() {
          super();
          this.items = new Map();
          this.activeTierIndex = -1;
          this._onCurrencyChange = this.formatPrices.bind(this);
        }

        connectedCallback() {
          this.dots = this.querySelectorAll('[data-slider-dot]');
          this.track = this.querySelector('.bundle__builder-slider-track');
          this.progress = this.querySelector('[data-slider-progress]');
          this.priceEls = this.querySelectorAll('[data-tier-discount-percent]');
          this.productRows = this.querySelectorAll('[data-builder-qty]');
          this.checkoutBtn = this.querySelector('[data-builder-checkout]');
          this.checkoutLabel = this.querySelector('[data-builder-checkout-label]');
          this.checkoutTotal = this.querySelector('[data-builder-checkout-total]');
          this.checkoutTexts = {
            empty: this.checkoutLabel?.textContent?.trim() || 'Select items to continue',
            ready: this.dataset.checkoutText || 'Checkout',
          };

          this.dots.forEach((dot, i) => {
            dot.addEventListener('click', () => this.setActiveTier(i));
          });

          this.productRows.forEach((row) => this.initProductRow(row));

          this.checkoutBtn?.addEventListener('click', () => this.checkout());

          this.formatPrices();
          this.updateFromTotal();

          ['currency:change', 'currency:changed', 'shopify:currency:change'].forEach((evt) => {
            document.addEventListener(evt, this._onCurrencyChange);
          });
        }

        disconnectedCallback() {
          ['currency:change', 'currency:changed', 'shopify:currency:change'].forEach((evt) => {
            document.removeEventListener(evt, this._onCurrencyChange);
          });
        }

        getCurrency() {
          return window.Shopify?.currency?.active || 'EUR';
        }

        getRate() {
          const rate = parseFloat(window.Shopify?.currency?.rate);
          return isNaN(rate) ? 1 : rate;
        }

        formatMoney(amount, currency) {
          try {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
          } catch (e) {
            return amount.toFixed(2);
          }
        }

        // Anchor price for the active currency (e.g. 15 for GBP). Falls back
        // to EUR base × Shopify.currency.rate when the active currency has no
        // configured anchor, so unknown markets still render a sensible value.
        getBaseUnit() {
          const currency = this.getCurrency();
          const key = 'basePrice' + currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase();
          const direct = parseFloat(this.dataset[key]);
          if (!isNaN(direct) && direct > 0) return direct;
          const eur = parseFloat(this.dataset.basePriceEur);
          if (isNaN(eur)) return null;
          return eur * this.getRate();
        }

        // Appstle Volume Discount ceils each line item to the nearest penny,
        // so we mirror that here (10.3125 → 10.32) to keep the slider price
        // and the actual checkout total in sync.
        ceilToPenny(amount) {
          return Math.ceil(amount * 100) / 100;
        }

        formatPrices() {
          const currency = this.getCurrency();
          const base = this.getBaseUnit();
          if (base === null) return;
          this.priceEls.forEach((el) => {
            const discount = parseFloat(el.dataset.tierDiscountPercent) || 0;
            const unit = this.ceilToPenny(base * (1 - discount / 100));
            const suffix = el.dataset.priceSuffix || '';
            const next = this.formatMoney(unit, currency) + suffix;
            if (el.textContent !== next) el.textContent = next;
          });
        }

        setActiveTier(index) {
          this.activeTierIndex = index;
          this.dots.forEach((dot, i) => dot.classList.toggle('is-active', index >= 0 && i <= index));
          const pct = index < 0 || this.dots.length <= 1 ? 0 : (index / (this.dots.length - 1)) * 100;
          if (this.progress) this.progress.style.width = pct + '%';
          if (this.track) this.track.style.setProperty('--bundle-progress', pct + '%');
        }

        initProductRow(row) {
          const addBtn = row.querySelector('[data-builder-add]');
          const controls = row.querySelector('[data-qty-controls]');
          const valueEl = row.querySelector('[data-qty-value]');
          const minusBtn = row.querySelector('[data-qty-minus]');
          const plusBtn = row.querySelector('[data-qty-plus]');
          const variantId = addBtn?.dataset.variantId;

          const setQty = (n) => {
            if (!variantId) return;
            if (n <= 0) {
              this.items.delete(variantId);
              controls.hidden = true;
              addBtn.hidden = false;
              valueEl.textContent = '0';
            } else {
              this.items.set(variantId, n);
              valueEl.textContent = String(n);
              addBtn.hidden = true;
              controls.hidden = false;
            }
            this.updateFromTotal();
          };

          addBtn?.addEventListener('click', () => setQty(1));
          plusBtn?.addEventListener('click', () => setQty((parseInt(valueEl.textContent, 10) || 0) + 1));
          minusBtn?.addEventListener('click', () => setQty((parseInt(valueEl.textContent, 10) || 0) - 1));
        }

        getTotalQuantity() {
          let total = 0;
          this.items.forEach((qty) => { total += qty; });
          return total;
        }

        getActiveUnit() {
          const dot = this.dots[this.activeTierIndex];
          if (!dot) return null;
          const base = this.getBaseUnit();
          if (base === null) return null;
          const idx = parseInt(dot.dataset.index, 10);
          const priceEl = this.priceEls[idx];
          const discount = parseFloat(priceEl?.dataset.tierDiscountPercent) || 0;
          return this.ceilToPenny(base * (1 - discount / 100));
        }

        updateFromTotal() {
          const total = this.getTotalQuantity();

          // Highest tier whose required quantity <= total. No tier active when nothing selected.
          let matched = -1;
          if (total > 0) {
            this.dots.forEach((dot, i) => {
              const q = parseInt(dot.dataset.tierQuantity, 10);
              if (!isNaN(q) && q > 0 && total >= q) matched = i;
            });
          }
          this.setActiveTier(matched);

          if (!this.checkoutBtn) return;
          this.checkoutBtn.disabled = total === 0;

          if (total === 0) {
            this.checkoutLabel.textContent = this.checkoutTexts.empty;
            if (this.checkoutTotal) this.checkoutTotal.hidden = true;
          } else {
            this.checkoutLabel.textContent = this.checkoutTexts.ready;
            const unit = this.getActiveUnit();
            if (this.checkoutTotal && unit !== null) {
              this.checkoutTotal.textContent = this.formatMoney(unit * total, this.getCurrency());
              this.checkoutTotal.hidden = false;
            }
          }
        }

        generateOfferToken() {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
          let out = '';
          for (let i = 0; i < 3; i++) out += chars[Math.floor(Math.random() * chars.length)];
          return out;
        }

        async checkout() {
          if (this.items.size === 0) return;

          const ebBundleId = this.dataset.ebBundleId;

          if (ebBundleId) {
            // Easy Bundle Builder flow: one line item per picked unit with the
            // OfferId/Box properties EB's app uses to apply its bundle discount.
            const bundleName = this.dataset.ebBundleName || 'Gel Builder';
            const token = this.generateOfferToken();
            const lineItems = [];
            let index = 1;
            this.items.forEach((qty, id) => {
              for (let i = 0; i < qty; i++) {
                lineItems.push({
                  id: parseInt(id, 10),
                  quantity: 1,
                  properties: {
                    Box: '1',
                    _bundleName: bundleName,
                    '_easyBundle:prodQty': 1,
                    '_easyBundle:OfferId': `${ebBundleId}_${token}_${index}`,
                  },
                });
                index++;
              }
            });

            try {
              const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: lineItems }),
              });
              if (!res.ok) throw new Error(`cart/add.js ${res.status}`);

              // Bundle apps (EB etc.) often wire their discount logic into
              // scripts that run on /cart. We load /cart silently in a hidden
              // iframe so those scripts execute, then send the real window to
              // /checkout — user never sees the cart page.
              await new Promise((resolve) => {
                const iframe = document.createElement('iframe');
                iframe.src = '/cart';
                iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
                iframe.onload = () => resolve();
                // Safety timeout: don't block checkout forever if /cart never fires load.
                setTimeout(resolve, 2500);
                document.body.appendChild(iframe);
              });

              window.location.href = '/checkout';
            } catch (e) {
              console.error('Bundle checkout failed', e);
              this.checkoutBtn.disabled = false;
            }
            return;
          }

          // Fallback: Shopify cart permalink with optional discount code.
          const parts = [];
          this.items.forEach((qty, id) => parts.push(`${id}:${qty}`));
          let url = `/cart/${parts.join(',')}`;
          const activeDot = this.dots[this.activeTierIndex];
          const discount = activeDot?.dataset.tierDiscount;
          if (discount) url += `?discount=${encodeURIComponent(discount)}`;
          window.location.href = url;
        }
      }
    );
  }

if (!customElements.get('pc-swatches')) {
  customElements.define(
    'pc-swatches',
    class VariantSwatches extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.section = document.getElementById('shopify-section-' + this.sectionId);
        this.swatches = Array.from(this.querySelectorAll('.bundle__swatch'));
        this.currentName = this.querySelector('[data-current-variant-name]');
        this.form = this.section?.querySelector('form[data-type="add-to-cart-form"]');
        this.idInput = this.form?.querySelector('input[name="id"]');
        this.submit = this.form?.querySelector('[type="submit"]');
        this.saleEl = this.section?.querySelector('[data-sale-price]');
        this.compareEl = this.section?.querySelector('[data-compare-price]');
        this.gallery = this.section?.querySelector('pc-gallery');

        // Per-variant gallery images keyed by variant id.
        this.variantGalleries = {};
        const dataEl = this.querySelector('[data-pc-variant-galleries]');
        if (dataEl) {
          try {
            this.variantGalleries = JSON.parse(dataEl.textContent);
          } catch (e) {
            console.error('Failed to parse variant galleries', e);
          }
        }

        this.swatches.forEach((btn) => {
          btn.addEventListener('click', () => this.select(btn));
        });
      }

      select(btn) {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        this.swatches.forEach((s) => {
          const active = s === btn;
          s.classList.toggle('bundle__swatch--active', active);
          s.setAttribute('aria-checked', active ? 'true' : 'false');
        });

        if (this.currentName) {
          this.currentName.textContent = btn.dataset.variantName || '';
        }

        if (this.idInput) {
          this.idInput.value = variantId;
        }

        if (this.saleEl) {
          this.saleEl.textContent = btn.dataset.variantPrice || '';
        }
        if (this.compareEl) {
          this.compareEl.textContent = btn.dataset.variantCompare || '';
        }

        const images = this.variantGalleries[variantId];
        if (this.gallery && images && images.length) {
          this.gallery.setImages(images);
        }

        const available = btn.dataset.variantAvailable !== 'false';
        if (this.submit) {
          this.submit.toggleAttribute('disabled', !available);
          this.submit.setAttribute('aria-disabled', available ? 'false' : 'true');
        }
      }
    }
  );
}

// Trust-badge shipping date: replace fallback with today + offset days
(function () {
  document.querySelectorAll('.bundle__trust-date[data-shipping-date]').forEach((el) => {
    const offset = parseInt(el.dataset.offset || '2', 10);
    const d = new Date();
    d.setDate(d.getDate() + offset);
    el.textContent = d.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  });
})();
